import * as Timeframes from "@/utils/timeframes";
import * as React from "react";

import type { ActivityContentGoalEditing } from "@/api";
import type { Activity } from "@/models/activities";
import type { ActivityHandler } from "../interfaces";

import { compareIds } from "@/routes/paths";
import { feedTitle, goalLink } from "../feedItemLinks";

const GoalEditing: ActivityHandler = {
  pageHtmlTitle(_activity: Activity) {
    throw new Error("Not implemented");
  },

  pagePath(paths, activity: Activity): string {
    return paths.goalPath(content(activity).goal!.id!);
  },

  PageTitle(_props: { activity: any }) {
    throw new Error("Not implemented");
  },

  PageContent(_props: { activity: Activity }) {
    throw new Error("Not implemented");
  },

  PageOptions(_props: { activity: Activity }) {
    return null;
  },

  FeedItemTitle({ activity, page }: { activity: Activity; page: any }) {
    if (page === "цель") {
      return feedTitle(activity, "изменил(а) цель");
    } else {
      return feedTitle(activity, "изменил(а)", goalLink(content(activity).goal!), "цель");
    }
  },

  FeedItemContent({ activity }: { activity: Activity }) {
    const c = content(activity);
    return (
      <div className="flex-flex-col gap-1">
        <NewName content={c} />
        <Timeframe content={c} />
        <Champion content={c} />
        <Reviewer content={c} />
        <AddedTargets content={c} />
        <UpdatedTargets content={c} />
        <DeletedTargets content={c} />
      </div>
    );
  },

  feedItemAlignment(_activity: Activity): "items-start" | "items-center" {
    return "items-start";
  },

  commentCount(_activity: Activity): number {
    throw new Error("Not implemented");
  },

  hasComments(_activity: Activity): boolean {
    throw new Error("Not implemented");
  },

  NotificationTitle({ activity }: { activity: Activity }) {
    return shortDesc(content(activity));
  },

  NotificationLocation({ activity }: { activity: Activity }) {
    return content(activity).goal!.name!;
  },
};

function content(activity: Activity): ActivityContentGoalEditing {
  return activity.content as ActivityContentGoalEditing;
}

export default GoalEditing;

function NewName({ content }: { content: ActivityContentGoalEditing }) {
  if (content.newName === content.oldName) return null;

  return <div>The name was changed to {content.newName}.</div>;
}

function Timeframe({ content }: { content: ActivityContentGoalEditing }) {
  const oldTimeframe = content.newTimeframe!;
  const newTimeframe = content.oldTimeframe!;

  if (Timeframes.equalDates(oldTimeframe, newTimeframe)) return null;

  return <div>The timeframe was changed to {Timeframes.getTimeframeRange(newTimeframe)}.</div>;
}

function Champion({ content }: { content: ActivityContentGoalEditing }) {
  if (compareIds(content.oldChampionId, content.newChampionId)) return null;

  return <div>The champion was changed to {content.newChampion!.fullName}.</div>;
}

function Reviewer({ content }: { content: ActivityContentGoalEditing }) {
  if (compareIds(content.oldReviewerId, content.newReviewerId)) return null;

  return <div>The reviewer was changed to {content.newReviewer!.fullName}.</div>;
}

function AddedTargets({ content }: { content: ActivityContentGoalEditing }) {
  if (!content.addedTargets) return null;
  if (content.addedTargets.length === 0) return null;

  return (
    <div className="not-first:mt-2">Были добавлены следующие показатели:<ul>
        {content.addedTargets.map((target) => (
          <li key={target!.id}>- {target!.name}</li>
        ))}
      </ul>
    </div>
  );
}

function UpdatedTargets({ content }: { content: ActivityContentGoalEditing }) {
  const updated = content.updatedTargets!.filter((t) => t!.oldName !== t!.newName);

  if (updated.length === 0) return null;

  return (
    <div className="not-first:mt-2">Были обновлены следующие показатели:<ul>
        {updated.map((target) => (
          <li key={target!.id}>- {target!.newName}</li>
        ))}
      </ul>
    </div>
  );
}

function DeletedTargets({ content }: { content: ActivityContentGoalEditing }) {
  if (!content.deletedTargets) return null;
  if (content.deletedTargets.length === 0) return null;

  return (
    <div className="not-first:mt-2">Были удалены следующие показатели:<ul>
        {content.deletedTargets.map((target) => (
          <li key={target!.id}>- {target!.name}</li>
        ))}
      </ul>
    </div>
  );
}

function shortDesc(content: ActivityContentGoalEditing): string {
  const { oldTimeframe, newTimeframe } = content;

  const changes = {
    name: content.oldName !== content.newName,
    timeframe: !Timeframes.equalDates(oldTimeframe, newTimeframe),
    champion: content.oldChampionId !== content.newChampionId,
    reviewer: content.oldReviewerId !== content.newReviewerId,
    measurements: content.addedTargets!.length + content.updatedTargets!.length + content.deletedTargets!.length > 0,
  };

  const activeChanges = Object.keys(changes).filter((key) => changes[key]);

  return "changed the goal " + joinChanges(activeChanges);
}

function joinChanges(changes: string[]): string {
  if (changes.length === 0) return "";
  if (changes.length === 1) return changes[0]!;
  if (changes.length === 2) return changes[0]! + " и " + changes[1]!;

  return changes.slice(0, -1).join(", ") + ", и " + changes[changes.length - 1]!;
}
